import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wand2, Send, PencilLine, Save, AlertTriangle, Facebook, Instagram, Music2 } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { StatusBadge } from "./StatusBadge";
import { BUSY, KIND_LABEL, dayLabel, topicTitle, videoSrc } from "./socialMeta";

export const PostDetail = ({ post, metaReady, onChangeTopic, reload }) => {
  const [caption, setCaption] = useState(post.caption || "");
  useEffect(() => setCaption(post.caption || ""), [post.id, post.caption]);
  const locked = ["published", "publishing", "generating"].includes(post.status);
  const act = async (path, msg) => {
    try { await api.post(`/admin/social/posts/${post.date}/${path}`); toast.success(msg); reload(); } catch (e) { toast.error(apiError(e)); }
  };
  const saveCaption = async () => {
    try { await api.put(`/admin/social/posts/${post.date}/caption`, { caption }); toast.success("Légende enregistrée"); reload(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <section data-testid="social-post-detail" className="bg-white border border-[#ECE7DE] rounded-2xl p-4 sm:p-6 grid md:grid-cols-[260px_1fr] gap-6">
      <div className="w-full max-w-[260px] mx-auto aspect-[9/16] rounded-2xl overflow-hidden bg-[#059669] flex items-center justify-center">
        {post.video_url
          ? <video key={post.video_url} data-testid="social-post-video" src={`${videoSrc(post.video_url)}#t=1`} preload="metadata" controls playsInline className="w-full h-full object-cover" />
          : <p className="text-white/90 text-sm text-center px-6">{BUSY.includes(post.status) ? "Création du clip IA et montage en cours (≈ 3 à 5 min)…" : "La vidéo sera générée le jour J à 7 h."}</p>}
      </div>
      <div className="min-w-0 space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-bold uppercase tracking-wide text-[#75807A]">{dayLabel(post.date)} · {post.publish_at?.slice(11, 16)}</span>
          <StatusBadge status={post.status} testId="social-post-status" />
        </div>
        <div>
          <div className="text-xs font-semibold text-[#059669]">{KIND_LABEL[post.topic?.kind]}</div>
          <h3 data-testid="social-post-topic" className="text-lg font-bold text-[#1A1A1A]">{topicTitle(post.topic)}</h3>
          {post.script?.hook && <p className="text-sm text-[#5A635D] mt-1">Accroche : « {post.script.hook} »</p>}
        </div>
        {(post.error || post.publish_error) && (
          <div data-testid="social-post-error" className="flex gap-2 text-sm rounded-xl bg-[#FEF1E7] text-[#9A3412] p-3">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /><span className="break-words min-w-0">{post.error || post.publish_error}</span>
          </div>
        )}
        {post.caption != null && (
          <div className="space-y-2">
            <Textarea data-testid="social-post-caption" value={caption} onChange={(e) => setCaption(e.target.value)} rows={6} disabled={locked} className="text-sm" />
            {!locked && caption !== post.caption && (
              <Button size="sm" variant="outline" onClick={saveCaption} data-testid="social-post-caption-save"><Save className="w-4 h-4 mr-1" />Enregistrer la légende</Button>
            )}
          </div>
        )}
        <div className="flex flex-wrap gap-2 text-xs text-[#5A635D]">
          {post.ai_clip && <span data-testid="social-post-aiclip" className="inline-flex items-center gap-1 text-[#D8531E] font-semibold">Clip IA</span>}
          {post.clip_error && <span data-testid="social-post-clip-error" className="text-[#9A3412]">Clip IA indisponible (montage seul) : {post.clip_error}</span>}
          {post.music && <span className="inline-flex items-center gap-1"><Music2 className="w-3.5 h-3.5" />{post.music}</span>}
          {post.duration && <span>{post.duration} s · 720×1280</span>}
          {post.fb_id && <a data-testid="social-post-fb" href={post.fb_id.startsWith("http") ? post.fb_id : undefined} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#1877F2] font-semibold hover:underline"><Facebook className="w-3.5 h-3.5" />Voir sur Facebook</a>}
          {post.ig_id && <a data-testid="social-post-ig" href={post.ig_id.startsWith("http") ? post.ig_id : undefined} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#C13584] font-semibold hover:underline"><Instagram className="w-3.5 h-3.5" />Voir sur Instagram</a>}
        </div>
        <div className="flex flex-wrap gap-2 pt-1">
          <Button variant="outline" disabled={locked} onClick={onChangeTopic} data-testid="social-change-topic-btn"><PencilLine className="w-4 h-4 mr-1" />Changer le sujet</Button>
          <Button variant="outline" disabled={locked} onClick={() => act("generate", "Génération lancée")} data-testid="social-generate-btn">
            <Wand2 className="w-4 h-4 mr-1" />{post.video_url ? "Régénérer" : "Générer maintenant"}
          </Button>
          {["ready", "partial"].includes(post.status) && (
            <Button disabled={!metaReady} onClick={() => act("publish", "Publication lancée")} data-testid="social-publish-btn"
              className="bg-[#F26419] hover:bg-[#D8531E] text-white"><Send className="w-4 h-4 mr-1" />Publier maintenant</Button>
          )}
        </div>
      </div>
    </section>
  );
};
